import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import {
  PayoutColumn,
  PayoutTabValues,
} from '../hooks/useAccountingPayoutsPage';

import type { LabelColor, UrlSort } from '@pflegenavi/web-components';
import {
  type ConfigType,
  Label,
  makeConfig,
  PaginatedTable2,
  Typography,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { CircularProgress, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { YEAR_SHORTMONTH_DAY_FORMAT } from '@pflegenavi/shared/constants';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import type {
  GetPayoutsResult,
  PayoutType,
} from '@pflegenavi/frontend/api-nursing-home';
import { useGetPayoutReconciliationPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { IconTooltip } from '@pflegenavi/transaction-components';
import { csvDownload } from '@pflegenavi/shared/frontend';
import { dateAsYYYY_MM_DD } from '@pflegenavi/shared/utils';
import { useSnackbar } from 'notistack';
import type { PayoutSortColumn } from '../AccountingPayoutsPageMangopay';

interface PayoutsTableProps {
  isLoading: boolean;
  payouts: GetPayoutsResult['data'] | undefined;
  nursingHomeId: string;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  tab: PayoutTabValues;
  onTabChange: (tab: PayoutTabValues) => void;
  sort: UrlSort<PayoutSortColumn>;
  onSortChange: (sort: UrlSort<PayoutSortColumn>) => void;
}

interface PayoutsTableRow {
  id: string;
  initiationDate: Date;
  transferDate: Date | undefined;
  ibanLast4: string;
  amount: number;
  payoutType: PayoutType;
  statusColor: LabelColor;
  statusString: string;
}

type ColumnNames =
  | PayoutColumn.ibanLast4
  | PayoutColumn.initiationDate
  | PayoutColumn.type
  | PayoutColumn.amount
  | PayoutColumn.transferDate
  | PayoutColumn.status
  | PayoutColumn.reconciliation;

type Filters = '';

export const AccountingPayoutsTableMangopay: FC<PayoutsTableProps> = ({
  isLoading,
  payouts,
  nursingHomeId,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  tab,
  onTabChange,
  sort,
  onSortChange,
}) => {
  const { t } = useTranslation();
  const fDate = useFormatDate();
  const { fCurrencyCents } = useFormatting();

  const rows = useMemo<PayoutsTableRow[]>(
    () =>
      payouts?.map((payout) => {
        const statusColor =
          payout.state === 'CREATED'
            ? 'warning'
            : payout.state === 'SUCCEEDED'
            ? 'success'
            : ('error' as LabelColor);
        const statusString =
          payout.state === 'CREATED'
            ? 'pending'
            : payout.state === 'SUCCEEDED'
            ? 'completed'
            : 'failed';

        return {
          id: payout.id,
          initiationDate: payout.inserted_at,
          transferDate: payout.transferred,
          ibanLast4: payout.iban_last_4,
          amount: payout.amount,
          payoutType: payout.payout_type,
          statusColor,
          statusString,
        };
      }) ?? [],
    [payouts]
  );

  const config: ConfigType<PayoutsTableRow, ColumnNames, Filters> = useMemo(
    () =>
      makeConfig<PayoutsTableRow, ColumnNames, Filters>({
        showAll: undefined,
        onClickRow: undefined,
        hoverRow: true,
        tabs: {
          selectedItem: tab,
          updateFilterModel: onTabChange,
          items: [
            {
              value: PayoutTabValues.All,
              label: t('accounting.payouts.states.all'),
              color: 'default',
              count: undefined,
            },
            {
              value: PayoutTabValues.Paid,
              label: t('accounting.payouts.states.completed'),
              color: 'success',
              count: undefined,
            },
            {
              value: PayoutTabValues.Pending,
              label: t('accounting.payouts.states.pending'),
              color: 'warning',
              count: undefined,
            },
            {
              value: PayoutTabValues.Failed,
              label: t('accounting.payouts.states.failed'),
              color: 'error',
              count: undefined,
            },
          ],
        },
        columns: [
          {
            key: PayoutColumn.ibanLast4,
            label: t('accounting.payouts.table-head.4-digits'),
            align: 'left',
            component: ({ row: { ibanLast4 } }) => (
              <Typography variant="subtitle2" noWrap>
                {ibanLast4 ? `**** ${ibanLast4}` : '--'}
              </Typography>
            ),
          },
          {
            key: PayoutColumn.initiationDate,
            label: t('accounting.payouts.table-head.initiation-date'),
            align: 'left',
            component: ({ row: { initiationDate } }) => (
              <Typography variant="subtitle2" noWrap>
                {fDate(new Date(initiationDate), YEAR_SHORTMONTH_DAY_FORMAT)}
              </Typography>
            ),
          },
          {
            key: PayoutColumn.type,
            label: t('accounting.payouts.table-head.type'),
            align: 'left',
            component: ({ row: { payoutType } }) => (
              <Typography variant="subtitle2" noWrap>
                {t(`accounting.payouts.type.${payoutType}`)}
              </Typography>
            ),
          },
          {
            key: PayoutColumn.amount,
            label: t('accounting.payouts.table-head.amount'),
            align: 'right',
            minWidth: 100,
            maxWidth: 150,
            sx: { paddingRight: 8 },
            component: ({ row: { amount } }) => (
              <Stack sx={{ paddingRight: 6 }}>
                <Typography variant="subtitle2">
                  {fCurrencyCents(amount)}
                </Typography>
              </Stack>
            ),
          },
          {
            key: PayoutColumn.transferDate,
            label: t('accounting.payouts.table-head.transfer-date'),
            align: 'left',
            onSort: (direction) =>
              onSortChange({
                column: 'transferred',
                direction: direction === 'ASC' ? 'asc' : 'desc',
              }),
            component: ({ row: { transferDate } }) => (
              <Typography variant="subtitle2" noWrap>
                {transferDate
                  ? fDate(new Date(transferDate), YEAR_SHORTMONTH_DAY_FORMAT)
                  : '--'}
              </Typography>
            ),
          },
          {
            key: PayoutColumn.status,
            label: t('accounting.payouts.table-head.status'),
            align: 'left',
            component: ({ row: { statusColor, statusString } }) => (
              <Label color={statusColor} variant="ghost">
                {t(`accounting.payouts.states.${statusString}`)}
              </Label>
            ),
          },
          {
            key: PayoutColumn.reconciliation,
            label: t('accounting.payouts.table-head.reconciliation'),
            align: 'left',
            component: ({ row: { id, initiationDate } }) => (
              <PayoutReconciliationDownload
                nursingHomeId={nursingHomeId}
                payoutId={id}
                initiationDate={initiationDate}
              />
            ),
          },
        ],
        sortColumn: {
          column: PayoutColumn.transferDate,
          direction: sort.direction === 'asc' ? 'ASC' : 'DESC',
        },
        emptyRowHeight: 77,
        pagination: {
          total: totalCount,
          page,
          onPageChange,
          pageSize,
          onPageSizeChange,
          pageSizeOptions: [5, 10, 25, 50],
        },
      }),
    [
      tab,
      onTabChange,
      t,
      totalCount,
      page,
      pageSize,
      onPageChange,
      onPageSizeChange,
      sort,
      onSortChange,
      nursingHomeId,
      fDate,
      fCurrencyCents,
    ]
  );

  return (
    <PaginatedTable2
      name="AccountingPayoutsTable"
      isLoading={isLoading}
      config={config}
      rows={rows}
    />
  );
};

interface PayoutReconciliationDownloadProps {
  nursingHomeId: string;
  payoutId: string;
  initiationDate: Date;
}

const PayoutReconciliationDownload: FC<PayoutReconciliationDownloadProps> = ({
  nursingHomeId,
  payoutId,
  initiationDate,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { fCsvNumberCents } = useFormatting();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { enqueueSnackbar } = useSnackbar();

  const { refetch, isFetching } = useGetPayoutReconciliationPhoenix(
    { params: { nursing_home_id: nursingHomeId, payout_id: payoutId } },
    { enabled: false }
  );

  const handleClick = useCallback(async () => {
    const result = await refetch();
    if (result.error || !result.data) {
      enqueueErrorSnackbar(
        result.error instanceof Error ? result.error : 'payout-reconciliation',
        t('dialog.payout-reconciliation.download-error')
      );
      return;
    }
    if (result.data.data.length === 0) {
      enqueueSnackbar(t('tooltip.no-reconciliation-available'), {
        variant: 'info',
      });
      return;
    }

    csvDownload({
      data: result.data.data.map((entry) => ({
        performedAt: entry.performed_at,
        transactionType: entry.mangopay_transaction_type,
        transactionNature: entry.mangopay_transaction_nature,
        amount: fCsvNumberCents(entry.amount),
        currency: entry.currency,
        residentFirstName: entry.receipt?.resident.firstname ?? null,
        residentLastName: entry.receipt?.resident.lastname ?? null,
        serviceProvider: entry.receipt?.service_provider_name ?? null,
        receiptType: entry.receipt?.receipt_type_name ?? null,
        receiptDate: entry.receipt?.receipt_date ?? null,
        mangopayResourceId: entry.mangopay_resource_id,
      })),
      filename: t('accounting.payouts.csv-export.filename', {
        initiationDate: dateAsYYYY_MM_DD(initiationDate),
      }),
      headers: [
        t('accounting.payouts.csv-export.performed-at'),
        t('accounting.payouts.csv-export.type'),
        t('accounting.payouts.csv-export.transaction-nature'),
        t('accounting.payouts.csv-export.amount'),
        t('accounting.payouts.csv-export.currency'),
        t('accounting.payouts.csv-export.resident-first-name'),
        t('accounting.payouts.csv-export.resident-last-name'),
        t('accounting.payouts.csv-export.service-provider'),
        t('accounting.payouts.csv-export.receipt-type'),
        t('accounting.payouts.csv-export.receipt-date'),
        t('accounting.payouts.csv-export.mangopay-resource-id'),
      ],
    });
  }, [
    refetch,
    enqueueErrorSnackbar,
    enqueueSnackbar,
    fCsvNumberCents,
    t,
    initiationDate,
  ]);

  return isFetching ? (
    <CircularProgress size={28} />
  ) : (
    <IconTooltip
      title={t('tooltip.download-reconciliation')}
      color={theme.palette.success.main}
      icon="check"
      onClick={handleClick}
    />
  );
};
